import * as React from 'react';
import { connect } from 'react-redux';
import { provideLocalizationService, registerForLocalization, provideIntlService, registerForIntl } from '@progress/kendo-react-intl';
import { Skeleton } from '@progress/kendo-react-indicators';
import { OverviewPage, StackLayout, Text, WrapLayout } from '../../../ui';
import { ValueItem, DateFormatter, InlineBadge } from '../../shared/components';
import {
  submitDesignation,
  approveDesignation,
  unapproveDesignation,
  cancelDesignation,
  loadDesignationOverviewPage,
  cleanupDesignationOverviewPage,
  toggleDesignationOverviewConfirmDialog,
  resubmitDesignation,
  removeDesignation
} from '../actions/designation-overview-page-actions';
import { withRouter } from '../../../components/withRouter';
import { DatePeriodFormatter } from '../../shared/components/Formatter';
import { getDesignationStatusColor } from '../../../utils/designationStatusColors';
import { Button, SplitButton } from '@progress/kendo-react-buttons';
import { DESIGNATION_STATUS } from '../../../resources/designationStatus';
import colors from '../../../ui/components/shared/colors';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { SvgIcon } from '@progress/kendo-react-common';
import { hyperlinkOpenIcon } from '@progress/kendo-svg-icons';
import styled from 'styled-components';
import { ROLE_PERMISSIONS } from '../../../resources/rolePermissions';

const DESIGNATION_ACTIONS = {
  DELETE: 'Delete',
  RESUBMIT: 'Resubmit'
};

const TitleWithRedirct = styled.span`
  cursor: pointer;
`;

class DesignationOverviewPage extends React.PureComponent {

  constructor() {
    super();
    this.handleBreadcrumbItemClick = this.handleBreadcrumbItemClick.bind(this);
    this.handleBackClick = this.handleBackClick.bind(this);
    this.renderSplitButtonItem = this.renderSplitButtonItem.bind(this);
    this.handleSplitButtonItemClick = this.handleSplitButtonItemClick.bind(this);
    this.handleTitleClick = this.handleTitleClick.bind(this);
  }

  async componentDidMount() {
    const { designationId, studyPlanId, studyPlanCycleId, studyPlanCycleStudentId, studyPlanCycleDepartmentStudentId } = this.props.params;
    if (designationId || (studyPlanId && studyPlanCycleId && studyPlanCycleStudentId && studyPlanCycleDepartmentStudentId)) {
      const localizationService = provideLocalizationService(this);
      this.props.loadDesignationOverviewPage({
        designationId,
        studyPlanId,
        studyPlanCycleId,
        studyPlanCycleStudentId,
        studyPlanCycleDepartmentStudentId,
        localizationService
      });
    }
  }

  componentWillUnmount() {
    if (this.props.designation?.designationPdfObjectUrl) {
      this.props.cleanupDesignationOverviewPage();
    }
  }

  render() {
    const { designation, loading } = this.props;
    const localizationService = provideLocalizationService(this);
    const intlService = provideIntlService(this);
    const canEditStudyPlans = this.props.userPermissions.includes(ROLE_PERMISSIONS.STUDY_PLANS_WRITE);
    return (
      <OverviewPage
        title={loading
          ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
          : canEditStudyPlans && designation?.canBeManaged && [DESIGNATION_STATUS.NOT_SUBMITTED, DESIGNATION_STATUS.FORMED].includes(designation?.status)
            ? <TitleWithRedirct onClick={this.handleTitleClick}>{designation?.department} <SvgIcon icon={hyperlinkOpenIcon} size='large'/></TitleWithRedirct>
            : <div>{designation?.department}</div>
        }
        breadcrumbItems={
          this.props.managerOverview
            ?
            [
              {
                id: 'designations',
                text: localizationService.toLanguageString('designations.designations')
              },
              {
                id: 'designation',
                text: localizationService.toLanguageString('designations.designation'),
                disabled: true
              }
            ]
            :
            [
              {
                id: 'designation',
                text: localizationService.toLanguageString('designations.designation'),
                disabled: true
              }
            ]
        }
        onBreadcrumbItemClick={this.handleBreadcrumbItemClick}
        successMessage={this.props.successMessage}
        errorMessage={this.props.errorMessage}
        headerActions={
          <>
            {(designation?.canBeManaged || this.props.userIsAdministrator) && DESIGNATION_STATUS.NOT_SUBMITTED === designation?.status &&
              <Button
                themeColor='primary'
                onClick={() => this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogSubmitDesignationText'), confirmAction: () => this.props.submitDesignation({ localizationService }) })}
              >
                {localizationService.toLanguageString('buttons.submit')}
              </Button>
            }
            {(designation?.canBeManaged || this.props.userIsAdministrator) && DESIGNATION_STATUS.APPROVED === designation?.status &&
              <Button
                themeColor='primary'
                onClick={() => this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogUnapproveDesignationText'), confirmAction: this.props.unapproveDesignation })}
              >
                {localizationService.toLanguageString('buttons.unapprove')}
              </Button>
            }
            {(this.props.userIsAdministrator) && DESIGNATION_STATUS.SIGNED === designation?.status &&
              <Button
                themeColor='primary'
                onClick={() => this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogCancelDesignationText'), confirmAction: () => this.props.cancelDesignation({ localizationService }) })}
              >
                {localizationService.toLanguageString('buttons.undo')}
              </Button>
            }
            {(designation?.canBeManaged || this.props.userIsAdministrator) && DESIGNATION_STATUS.FORMED === designation?.status &&
              <SplitButton
                themeColor={'primary'}
                text={localizationService.toLanguageString('buttons.confirm')}
                items={this.renderSplitButtonItem()}
                onButtonClick={() => this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogApproveDesignationText'), confirmAction: this.props.approveDesignation })}
                onItemClick={this.handleSplitButtonItemClick}
              />
            }
          </>
        }
        onBackClick={this.handleBackClick}
        summary={
          <StackLayout rowGap='8px'>
            <ValueItem
              label={localizationService.toLanguageString('designations.date')}
              labelWidth='100px'
              loading={loading}
            >
              {designation?.accreditationDateFrom && designation?.accreditationDateTo ? DatePeriodFormatter(designation.accreditationDateFrom, designation.accreditationDateTo, intlService) : ''}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('designations.cycle')}
              labelWidth='100px'
              loading={loading}
            >
              {designation?.cycle}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('designations.credits')}
              labelWidth='100px'
              loading={loading}
            >
              {designation?.accreditationCredits}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('designations.statusHistory')}
              labelWidth='100px'
              loading={loading}
            >
              {designation?.designationHistory?.length > 0
                ?
                <WrapLayout orientation='vertical'>
                  {designation.designationHistory.map((element, index) => {
                    return (
                      <div key={index}>
                        <InlineBadge themeColor={getDesignationStatusColor(element.status)}>
                          {localizationService.toLanguageString('designationStatus.' + element.status)}
                        </InlineBadge>
                        <Text variant={'body2'} textColor={colors.GRAY_80}>
                          {` ${element.createdBy} ${DateFormatter(element.date, intlService)}`}
                        </Text>
                      </div>
                    );
                  })}
                </WrapLayout>
                :
                <InlineBadge themeColor={getDesignationStatusColor(DESIGNATION_STATUS.NOT_SUBMITTED)}>
                  {localizationService.toLanguageString(`designationStatus.${DESIGNATION_STATUS.NOT_SUBMITTED}`)}
                </InlineBadge>
              }
            </ValueItem>
          </StackLayout>
        }
      >
        {this.props.isConfirmDialogVisible &&
          <Dialog
            title={localizationService.toLanguageString('custom.confirm')} onClose={this.props.toggleDesignationOverviewConfirmDialog}>
            <Text>
              {this.props.confirmDialogText}
            </Text>
            <DialogActionsBar>
              <Button
                onClick={() => {
                  this.props.toggleDesignationOverviewConfirmDialog();
                  this.props.confirmAction();
                }}
                themeColor='primary'
              >
                {localizationService.toLanguageString('buttons.yes')}
              </Button>
              <Button
                onClick={this.props.toggleDesignationOverviewConfirmDialog}>{localizationService.toLanguageString('buttons.no')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
        {!loading && designation?.designationPdfObjectUrl &&
          <iframe
            src={designation?.designationPdfObjectUrl}
            title={localizationService.toLanguageString('designations.designation')}
            width={'100%'}
            height={'1000'}
          />
        }
      </OverviewPage>
    );
  }

  handleBreadcrumbItemClick(e) {
    if (e.id === 'designations') {
      this.props.navigate('/designations');
    }
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleTitleClick() {
    const { studyPlanId, studyPlanCycleId, studyPlanCycleStudentId, studyPlanCycleDepartmentStudentId } = this.props.designation;
    if (studyPlanId && studyPlanCycleId && studyPlanCycleStudentId && studyPlanCycleDepartmentStudentId) {
      this.props.navigate(`/study_plans/${studyPlanId}/cycles/${studyPlanCycleId}/students/${studyPlanCycleStudentId}/departments/${studyPlanCycleDepartmentStudentId}`);
    }
  }

  renderSplitButtonItem() {
    const items = [];
    const localizationService = provideLocalizationService(this);
    items.push({ id: DESIGNATION_ACTIONS.RESUBMIT, text: localizationService.toLanguageString('buttons.resubmit') });
    items.push({ id: DESIGNATION_ACTIONS.DELETE, text: localizationService.toLanguageString('buttons.undo') });
    return items;
  }

  handleSplitButtonItemClick(e) {
    const localizationService = provideLocalizationService(this);
    if (e.item.id === DESIGNATION_ACTIONS.RESUBMIT) {
      this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogResubmitDesignationText'), confirmAction: () => this.props.resubmitDesignation({ localizationService }) });
    }
    if (e.item.id === DESIGNATION_ACTIONS.DELETE) {
      this.props.toggleDesignationOverviewConfirmDialog({ confirmDialogText: localizationService.toLanguageString('designations.confirmDialogDeleteDesignationText'), confirmAction: this.props.removeDesignation });
    }
  }
}

registerForLocalization(DesignationOverviewPage);
registerForIntl(DesignationOverviewPage);

const mapStateToProps = state => ({
  userPermissions: state.app.userPermissions,
  userIsAdministrator: state.app.userIsAdministrator,
  successMessage: state.designationOverviewPage.successMessage,
  errorMessage: state.designationOverviewPage.errorMessage,
  designation: state.designationOverviewPage.designation,
  loading: state.designationOverviewPage.loading,
  isConfirmDialogVisible: state.designationOverviewPage.isConfirmDialogVisible,
  confirmDialogText: state.designationOverviewPage.confirmDialogText,
  confirmAction: state.designationOverviewPage.confirmAction
});

const mapDispatchToProps = dispatch => ({
  loadDesignationOverviewPage: (payload) => dispatch(loadDesignationOverviewPage(payload)),
  submitDesignation: (payload) => dispatch(submitDesignation(payload)),
  approveDesignation: () => dispatch(approveDesignation()),
  unapproveDesignation: () => dispatch(unapproveDesignation()),
  cancelDesignation: (payload) => dispatch(cancelDesignation(payload)),
  cleanupDesignationOverviewPage: () => dispatch(cleanupDesignationOverviewPage()),
  toggleDesignationOverviewConfirmDialog: (payload) => dispatch(toggleDesignationOverviewConfirmDialog(payload)),
  resubmitDesignation: (payload) => dispatch(resubmitDesignation(payload)),
  removeDesignation: () => dispatch(removeDesignation())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(DesignationOverviewPage));
